/**
 * Cliente HTTP de sugerencias de respuesta ("Inspirar respuesta") para
 * `apps/speak`.
 *
 * On-demand: cuando el user toca la bombita y no sabe qué contestar, mandamos
 * los últimos turnos de la conversación (que ya tenemos en `useVoiceSession`)
 * y el backend genera 3 ideas con LLM, cada una con su traducción al español.
 *
 * Transporte: el cliente tipado compartido de la familia
 * (`createTypedApiClient`), como los otros 11 wrappers de speak. Lo propio de
 * éste es el error (ver `SuggestError`) y que la respuesta se VALIDA en vez de
 * desenvolverse a ciegas.
 *
 * Backend contract:
 *   POST /api/modules/english/suggest-replies
 *     { messages: [{role:'user'|'assistant', content}], sessionId? }
 *     -> 200 { success, data: { suggestions: [{en, es}] } }
 *        400 invalid_input | 402 insufficient_credits
 *        502 generation_failed/provider_resolution_failed | 503 unavailable
 */

import { createTypedApiClient, TypedApiError } from '@orborix/auth-client';

const BASE = '/api/modules/english';

/** Ángulo de la sugerencia (Fase 14C) — para mostrar un label y diferenciarlas. */
export type InspireAngle = 'direct' | 'detail' | 'question';

/** Una idea de respuesta: inglés (lo que el alumno podría decir) + traducción + ángulo. */
export interface InspireSuggestion {
  en: string;
  es: string;
  /** Ausente en respuestas legacy; el cliente degrada sin label. */
  angle?: InspireAngle;
  /**
   * Charla asistida: en la «direct», el objetivo pendiente que hace la frase
   * (índice en los `goals` pedidos). Ausente = ninguno.
   */
  goal?: number;
}

/** Turno de la conversación enviado como contexto al backend. */
export interface SuggestMessage {
  role: 'user' | 'assistant';
  content: string;
}

const ERROR_MESSAGES: Record<string, string> = {
  unavailable: 'Las sugerencias no están disponibles por ahora.',
  insufficient_credits: 'No te quedan créditos para generar ideas.',
  generation_failed: 'No pudimos generar ideas. Probá de nuevo.',
  provider_resolution_failed: 'No pudimos generar ideas. Probá de nuevo.',
  invalid_input: 'No pudimos generar ideas con este contexto.',
  network_error: 'No pudimos conectar con el servidor. Revisá tu conexión.',
};

const GENERIC_MESSAGE = 'No pudimos generar ideas. Probá de nuevo en un momento.';

/**
 * Error con mensaje user-facing en español + código crudo del backend.
 *
 * A diferencia de los otros wrappers de speak, el `message` **no** es el del
 * backend: `useInspire` pinta `err.message` tal cual bajo la bombita, así que
 * el copy tiene que salir del CODE. Por eso el mensaje que le pasa el cliente
 * compartido (el `error` crudo del body, o su "no pudimos conectar" genérico)
 * se descarta y se traduce acá — incluido el `network_error`, que en esta
 * pantalla pide revisar la conexión.
 *
 * `details` (el body crudo del error) lo suma la base y es nuevo: antes esta
 * clase lo tiraba.
 */
export class SuggestError extends TypedApiError {
  constructor(
    _serverMessage: string,
    status: number,
    code: string,
    details: Record<string, unknown> = {},
  ) {
    super(ERROR_MESSAGES[code] ?? GENERIC_MESSAGE, status, code, details);
    this.name = 'SuggestError';
  }
}

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'SuggestError',
  // La bombita no cancela: no hay `AbortSignal` en esta superficie. El `wrap`
  // conserva lo que hacía el `try/catch` que envolvía al `fetch`.
  abortError: 'wrap',
  errorClass: SuggestError,
});

interface SuggestResponse {
  data: { suggestions: InspireSuggestion[] };
}

function isSuggestResponse(value: unknown): value is SuggestResponse {
  if (typeof value !== 'object' || value === null) return false;
  const data = (value as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return false;
  const suggestions = (data as { suggestions?: unknown }).suggestions;
  return (
    Array.isArray(suggestions) &&
    suggestions.every(
      (s) =>
        typeof s === 'object' &&
        s !== null &&
        typeof (s as InspireSuggestion).en === 'string' &&
        typeof (s as InspireSuggestion).es === 'string' &&
        ((s as InspireSuggestion).goal === undefined ||
          Number.isInteger((s as InspireSuggestion).goal)),
    )
  );
}

/**
 * Pide al backend hasta 3 sugerencias de respuesta para el contexto dado.
 * Lanza `SuggestError` con mensaje en español si falla.
 *
 * No usa el `send()` del cliente: la respuesta se VALIDA (`isSuggestResponse`)
 * en vez de desenvolverse a ciegas, porque un `{data:{}}` a medias pintaría
 * una lista vacía de ideas en vez de un error.
 */
export async function fetchReplySuggestions(
  messages: ReadonlyArray<SuggestMessage>,
  sessionId?: string,
  /** Charla asistida: los objetivos de la escena todavía sin practicar. */
  goals?: ReadonlyArray<string>,
  /**
   * Charla asistida: la «direct» es la frase que el alumno DICE (una oración corta).
   * `avoid` = las que descartó para este turno: la nueva es otra, más simple.
   */
  opts?: { assisted?: boolean; pushGoal?: boolean; avoid?: ReadonlyArray<string> },
): Promise<InspireSuggestion[]> {
  const res = await client.request('/suggest-replies', {
    method: 'POST',
    body: JSON.stringify({
      messages,
      ...(sessionId ? { sessionId } : {}),
      ...(goals && goals.length > 0 ? { goals } : {}),
      ...(opts?.assisted ? { assisted: true } : {}),
      ...(opts?.pushGoal ? { pushGoal: true } : {}),
      ...(opts?.avoid && opts.avoid.length > 0 ? { avoid: opts.avoid } : {}),
    }),
  });
  await client.ensureOk(res);

  // `catch(() => null)`: un 200 con body no-JSON cae en `malformed_response`,
  // no en el `SyntaxError` crudo del parseo.
  const body: unknown = await res.json().catch(() => null);
  if (!isSuggestResponse(body)) {
    throw new SuggestError('', res.status, 'malformed_response');
  }
  return body.data.suggestions;
}

/**
 * «Descartar esta frase» en la charla asistida (Emmanuel, 19/10): el server sólo
 * la anota, para revisar al escritor. Tira si no se pudo (el cliente lo avisa en
 * la consola; la frase se descarta igual).
 */
export async function discardAssistedPhrase(input: {
  sessionId: string;
  phrase: string;
  translation?: string;
  scenarioId?: string;
}): Promise<void> {
  const res = await client.request('/assisted-phrases/discard', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  await client.ensureOk(res);
}
